import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { CustomEase } from "gsap/CustomEase";

gsap.registerPlugin(ScrollTrigger, CustomEase);

// Ease "dolce" condivisa da tutte le animazioni dell'intro: cubic-bezier(0.65,0,0.35,1).
CustomEase.create("sweet", "M0,0 C0.65,0 0.35,1 1,1");

const INTRO_KEY = "heroSplitIntroSeen";
const INTRO_CLASS = "hero-split-intro";
const MINI_CLASS = "hero-split-mini";

// Timeline d'ingresso: la finestra del logo (mask-size) cresce su un'immagine
// ferma finche' la riempie a tutto schermo; poi le colonne (sx dall'alto, dx dal
// basso) coprono e infine titolo/sottotitolo in fade. Al termine sblocca lo scroll.
const THUMB_H = 4.5; // altezza thumbnail nella striscia iniziale (rem)
const THUMB_GAP = 1; // gap tra le thumbnail (rem)

const runIntro = (section, onDone) => {
  const intro = section.querySelector(".hero-split__intro");
  const mask = section.querySelector(".hero-split__intro-mask");
  const scrim = section.querySelector(".hero-split__intro-scrim");
  const introLogo = section.querySelector(".hero-split__intro-logo");
  const thumbs = Array.from(section.querySelectorAll(".hero-split__intro-thumb"));
  const colSx = section.querySelector(".hero-split__col--sx");
  const colDx = section.querySelector(".hero-split__col--dx");
  const overlay = section.querySelector(".hero-split__overlay");

  const finish = () => {
    document.documentElement.classList.remove(INTRO_CLASS);
    if (intro) intro.remove();
    try {
      sessionStorage.setItem(INTRO_KEY, "1");
    } catch (e) {}
    onDone();
    // Segnala che l'intro (e il pin) sono pronti: i reveal delle sezioni sotto
    // vanno creati ORA, con le posizioni corrette (vedi reveal.js).
    document.dispatchEvent(new CustomEvent("hero-split:intro-done"));
  };

  if (!mask) {
    finish();
    return;
  }

  const tl = gsap.timeline({ defaults: { ease: "sweet" }, onComplete: finish });

  // Geometria della striscia: 5 elementi a viewport pieno (mask + 4 thumb) scalati
  // a un'altezza di THUMB_H rem e disposti in riga centrata (mask al centro, pos 0).
  const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const thumbScale = (THUMB_H * rootFont) / window.innerHeight;
  const thumbW = window.innerWidth * thumbScale;
  const step = thumbW + THUMB_GAP * rootFont;
  const thumbAt = (pos) => thumbs.find((t) => parseInt(t.dataset.pos, 10) === pos);

  // Stato iniziale: scala thumbnail, visibili ma nascoste dal clip (mask chiusa a
  // destra). Nessuno slide dal bordo del viewport.
  gsap.set([mask, ...thumbs], {
    scale: thumbScale,
    autoAlpha: 1,
    clipPath: "inset(0% 0% 0% 100%)",
  });

  // Nastro "mascherato": ordine d'ingresso da sinistra a destra della fila finale
  // (la centrale = mask e' la 3a). Ogni nuova immagine APPARE al suo slot con un
  // reveal da destra a sinistra (clip-path), le esistenti scorrono a sinistra per
  // ricentrare il gruppo di k (j-esima a (j - (k-1)/2)*step). Sempre centrato.
  const entryOrder = [thumbAt(-2), thumbAt(-1), mask, thumbAt(1), thumbAt(2)].filter(Boolean);
  const n = entryOrder.length;
  const ENTER_DUR = 1.2;
  const STAGE = 0.5; // tempo tra l'ingresso di un'immagine e la successiva
  const START = 0.2;

  entryOrder.forEach((_, kIdx) => {
    const startT = START + kIdx * STAGE;
    for (let j = 0; j <= kIdx; j++) {
      const el = entryOrder[j];
      const xTarget = (j - kIdx / 2) * step;
      if (j === kIdx) {
        // nuova: piazzala al suo slot (clip nascosto) e rivelala da destra a sinistra
        tl.set(el, { x: xTarget }, startT);
        tl.to(el, { clipPath: "inset(0% 0% 0% 0%)", duration: ENTER_DUR, ease: "power4.out" }, startT);
      } else {
        // esistenti: scorrono a sinistra per ricentrare il gruppo
        tl.to(el, { x: xTarget, duration: ENTER_DUR, ease: "power4.out" }, startT);
      }
    }
  });

  // Fase zoom: a striscia completa, la centrale zooma a tutto schermo (le laterali
  // restano e vengono coperte); in parallelo lo scrim 0 -> .8.
  // Nessuna pausa: lo zoom aggancia la fine della striscia in leggera sovrapposizione.
  const stripEnd = START + (n - 1) * STAGE + ENTER_DUR;
  tl.to(mask, { scale: 1, x: 0, duration: 0.8, ease: "power4.Out" }, stripEnd - 0.25);
  if (scrim)
    tl.fromTo(scrim, { opacity: 0 }, { opacity: 0.8, duration: 1, ease: "power4.inOut" }, "<");

  // Fase 2: finito lo zoom, il logo compare (0 -> 1) sopra l'immagine scurita.
  if (introLogo)
    tl.fromTo(introLogo, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1, ease: "power4.inOut" }, ">");

  // Fase 3: solo quando il logo e' a 1 partono le colonne (sx dal basso, dx dall'alto).
  // fromTo con y:0 esplicito: GSAP usa lo yPercent e non eredita il px del transform CSS.
  if (colSx)
    tl.fromTo(colSx, { yPercent: 100, y: 0 }, { yPercent: 0, duration: 1.2, ease: "power4.out" }, ">0.5");
  if (colDx)
    tl.fromTo(colDx, { yPercent: -100, y: 0 }, { yPercent: 0, duration: 1.2, ease: "power4.out" }, "<0.3");

  // Fase 4: SOLO a colonne finite, entrano in stagger (fade + salita) sottotitolo,
  // titolo e menu (header). Lo scrim dell'overlay sfuma col contenitore.
  const header = document.querySelector(".site-header");
  const brand = section.querySelector(".hero-split__brand");
  const subtitle = overlay ? overlay.querySelector(".hero-split__subtitle") : null;
  const title = overlay ? overlay.querySelector(".hero-split__title") : null;
  const text = overlay ? overlay.querySelector(".hero-split__text") : null;
  const meta = section.querySelector(".hero-split__meta");
  const revealItems = [brand, subtitle, title, text, header, meta].filter(Boolean);

  gsap.set(revealItems, { autoAlpha: 0, y: 30 });
  if (overlay)
    tl.to(overlay, { autoAlpha: 1, duration: 0.5, ease: "sweet" }, ">");
  // Logo in alto -> sottotitolo -> (pausa) -> titolo -> paragrafo -> menu (stessa dissolvenza).
  if (brand)
    tl.to(brand, { autoAlpha: 1, y: 0, duration: 1.4, ease: "sweet" }, overlay ? "<0.15" : ">");
  if (subtitle)
    tl.to(
      subtitle,
      { autoAlpha: 1, y: 0, duration: 1.4, ease: "sweet" },
      brand ? "<0.35" : overlay ? "<0.15" : ">"
    );
  if (title)
    tl.to(title, { autoAlpha: 1, y: 0, duration: 1.4, ease: "sweet" });
  if (text)
    tl.to(text, { autoAlpha: 1, y: 0, duration: 1.4, ease: "sweet" }, title ? "<0.3" : ">");
  if (header)
    tl.to(header, { autoAlpha: 1, y: 0, duration: 1.4, ease: "sweet" }, "<0.4");
  // Barra info: ultimo elemento a comparire.
  if (meta)
    tl.to(meta, { autoAlpha: 1, y: 0, duration: 1.4, ease: "sweet" }, header ? "<0.2" : ">");
};

// Modalita' singola (pagine interne): una sola immagine divisa in due meta'.
// Intro CORTA riprodotta a OGNI caricamento (niente gate a sessione): le due meta'
// appaiono in dissolvenza con micro-scostamento opposto (sx dal basso, dx dall'alto)
// che richiama la ricomposizione, poi brand -> sottotitolo -> titolo in fade.
// Niente striscia/logo/zoom, niente pin. Il pre-hide anti-FOUC (classe hero-split-mini)
// e' rimosso a fine timeline.
const runSingleIntro = (section, reduce) => {
  const html = document.documentElement;
  if (reduce || !html.classList.contains(MINI_CLASS)) {
    html.classList.remove(MINI_CLASS);
    return;
  }

  const colSx = section.querySelector(".hero-split__col--sx");
  const colDx = section.querySelector(".hero-split__col--dx");
  const overlay = section.querySelector(".hero-split__overlay");
  const brand = section.querySelector(".hero-split__brand");
  const subtitle = overlay ? overlay.querySelector(".hero-split__subtitle") : null;
  const title = overlay ? overlay.querySelector(".hero-split__title") : null;
  const text = overlay ? overlay.querySelector(".hero-split__text") : null;
  const meta = section.querySelector(".hero-split__meta");

  const tl = gsap.timeline({
    defaults: { ease: "sweet" },
    onComplete: () => html.classList.remove(MINI_CLASS),
  });
  if (colSx) tl.fromTo(colSx, { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.9 }, 0);
  if (colDx) tl.fromTo(colDx, { autoAlpha: 0, y: -30 }, { autoAlpha: 1, y: 0, duration: 0.9 }, 0.15);
  const items = [brand, subtitle, title, text, meta].filter(Boolean);
  gsap.set(items, { autoAlpha: 0, y: 20 });
  if (overlay) tl.to(overlay, { autoAlpha: 1, duration: 0.4 }, "<");
  if (brand) tl.to(brand, { autoAlpha: 1, y: 0, duration: 1 }, overlay ? "<0.1" : ">");
  if (subtitle) tl.to(subtitle, { autoAlpha: 1, y: 0, duration: 1 }, brand ? "<0.25" : ">");
  if (title) tl.to(title, { autoAlpha: 1, y: 0, duration: 1 });
  if (text) tl.to(text, { autoAlpha: 1, y: 0, duration: 1 }, title ? "<0.25" : ">");
  // Barra info: ultimo elemento a comparire.
  if (meta) tl.to(meta, { autoAlpha: 1, y: 0, duration: 1 }, text ? "<0.25" : ">");
};

const heroSplit = () => {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  document.querySelectorAll(".hero-split").forEach((section) => {
    if (section.classList.contains("hero-split--single")) {
      runSingleIntro(section, reduce);
      return;
    }

    const trackSx = section.querySelector('[data-hero-split-track="sx"]');
    const trackDx = section.querySelector('[data-hero-split-track="dx"]');
    if (!trackSx || !trackDx) return;

    const count = trackSx.querySelectorAll(".hero-split__slide").length;
    // Catturato PRIMA del pin: dopo, ScrollTrigger avvolge la sezione in un
    // .pin-spacer e section.nextElementSibling non sarebbe piu' la sezione dopo.
    const nextSection = section.nextElementSibling;

    // Pin + snap slide-per-slide. Avviato solo a intro conclusa, cosi' le misure
    // del pin sono corrette (colonne gia' in posizione, scroll sbloccato).
    const setupScroll = () => {
      if (reduce || count < 2) return;

      // yPercent e' relativo all'altezza del track (N slide): un passo = 100/N.
      const dist = (100 * (count - 1)) / count;

      // y: 0 azzera l'offset px ereditato dal transform CSS del track dx (anti-FOUC),
      // altrimenti GSAP lo sommerebbe allo yPercent (doppio spostamento).
      gsap.set(trackSx, { y: 0, yPercent: 0 });
      gsap.set(trackDx, { y: 0, yPercent: -dist });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: () => "+=" + (count - 1) * window.innerHeight,
          scrub: true,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          // Snap slide-per-slide: al rilascio scatta allo step piu' vicino
          // (directional: false => soglia a meta' viewport, non semplice direzione).
          snap: {
            snapTo: 1 / (count - 1),
            duration: { min: 0.2, max: 0.5 },
            ease: "sweet",
            directional: false,
          },
        },
      });

      tl.to(trackSx, { yPercent: -dist, ease: "none" }, 0).to(
        trackDx,
        { yPercent: 0, ease: "none" },
        0
      );

      ScrollTrigger.refresh();
    };

    // Quando la sezione successiva entra in viewport, sottotitolo e titolo (in
    // quest'ordine) fanno la dissolvenza AL CONTRARIO (fade out + discesa). Torna
    // indietro se si risale (reverse).
    const setupExitFade = () => {
      if (reduce || !nextSection) return;
      const overlay = section.querySelector(".hero-split__overlay");
      if (!overlay) return;
      const subtitle = overlay.querySelector(".hero-split__subtitle");
      const title = overlay.querySelector(".hero-split__title");
      const text = overlay.querySelector(".hero-split__text");
      const meta = section.querySelector(".hero-split__meta");
      if (!subtitle && !title && !text && !meta) return;

      const tl = gsap.timeline({ paused: true, defaults: { ease: "sweet" } });
      if (meta) tl.to(meta, { autoAlpha: 0, y: 30, duration: 0.4 }, 0);
      if (subtitle) tl.to(subtitle, { autoAlpha: 0, y: 30, duration: 0.4 }, 0);
      if (title) tl.to(title, { autoAlpha: 0, y: 30, duration: 0.4 }, 0.25);
      if (text) tl.to(text, { autoAlpha: 0, y: 30, duration: 0.4 }, 0.4);

      ScrollTrigger.create({
        trigger: nextSection,
        start: "top 80%",
        onEnter: () => tl.play(),
        onLeaveBack: () => tl.reverse(),
      });
      ScrollTrigger.refresh();
    };

    const wantsIntro =
      document.documentElement.classList.contains(INTRO_CLASS) &&
      section.querySelector(".hero-split__intro");

    if (wantsIntro && !reduce && count >= 1) {
      runIntro(section, () => {
        setupScroll();
        setupExitFade();
      });
    } else {
      // Nessuna intro (gia' vista, reduced motion, o niente coppie): stato normale.
      document.documentElement.classList.remove(INTRO_CLASS);
      setupScroll();
      setupExitFade();
    }
  });
};

export default heroSplit;
